// "Depends on" — an initiative can name the one it follows (a POC before the
// migration; 50% coverage before 80%). The API rejects self-links and loops;
// these helpers keep the picker from offering them in the first place and
// let the timeline keep a chain on one row.

import type { Initiative } from "./types";

// Every initiative that (directly or transitively) depends on `id`.
export function dependentsOf(id: string, all: Initiative[]): Set<string> {
  const children = new Map<string, string[]>();
  for (const i of all) {
    if (!i.dependsOnId) continue;
    const list = children.get(i.dependsOnId) ?? [];
    list.push(i.id);
    children.set(i.dependsOnId, list);
  }
  const out = new Set<string>();
  const stack = [...(children.get(id) ?? [])];
  while (stack.length) {
    const cur = stack.pop()!;
    if (out.has(cur)) continue;
    out.add(cur);
    stack.push(...(children.get(cur) ?? []));
  }
  return out;
}

// What `id` may depend on: anything except itself and its own dependents
// (picking one of those would close a loop).
export function dependencyCandidates(id: string, all: Initiative[]): Initiative[] {
  const blocked = dependentsOf(id, all);
  blocked.add(id);
  return all.filter(i => !blocked.has(i.id));
}

// Direct dependents only — the drawer's "Followed by" list.
export function directDependents(id: string, all: Initiative[]): Initiative[] {
  return all.filter(i => i.dependsOnId === id);
}

// Group a set of initiatives into dependency chains. Links only count when
// both ends are in the set (a predecessor in another lane, outside the
// window, or archived doesn't pull anything along). Returns groups in
// input order of their first member; unlinked items are groups of one.
export function dependencyGroups<T extends Initiative>(items: T[]): T[][] {
  const byId = new Map(items.map(i => [i.id, i]));
  const rootOf = (i: T): string => {
    const seen = new Set<string>();
    let cur = i;
    while (cur.dependsOnId && byId.has(cur.dependsOnId) && !seen.has(cur.id)) {
      seen.add(cur.id);
      cur = byId.get(cur.dependsOnId)!;
    }
    return cur.id;
  };
  const groups = new Map<string, T[]>();
  for (const i of items) {
    const r = rootOf(i);
    const g = groups.get(r) ?? [];
    g.push(i);
    groups.set(r, g);
  }
  return Array.from(groups.values());
}

// Gantt-style finish-to-start connectors: from the end of each predecessor
// bar to the start of its dependent, for pairs placed in the same lane.
// Coordinates are pixels inside the lane canvas. Lines run in the lower part
// of the bar (under the centered label) so they never read as a strikethrough.
export type ConnectorBar = { id: string; dependsOnId?: string; _left: number; _right: number; _top: number; _group?: string };

export const CONNECTOR_STEP = 8; // horizontal stub out of / into a bar

// `cross` marks a link between different groups (e.g. two pods) — drawn
// lighter so it reads as background to the grouping.
export function connectorPaths(bars: ConnectorBar[], canvasWidth: number, barHeight: number): { id: string; d: string; cross: boolean }[] {
  const byId = new Map(bars.map(b => [b.id, b]));
  const y = (b: ConnectorBar) => b._top + barHeight - 6;
  const out: { id: string; d: string; cross: boolean }[] = [];
  for (const b of bars) {
    const a = b.dependsOnId ? byId.get(b.dependsOnId) : undefined;
    if (!a) continue;
    const ax = (a._right * canvasWidth) / 100;
    const bx = (b._left * canvasWidth) / 100;
    const ay = y(a);
    const by = y(b);
    let d: string;
    if (ay === by && bx >= ax) {
      // Same row, in order: a straight run across the gap.
      d = `M ${ax} ${ay} H ${bx}`;
    } else if (bx - ax >= 2 * CONNECTOR_STEP) {
      // Different rows with room between: out, down/up, in.
      const xm = ax + CONNECTOR_STEP;
      d = `M ${ax} ${ay} H ${xm} V ${by} H ${bx}`;
    } else {
      // The dependent starts before the predecessor ends: step out, cross
      // over in the gap beside the dependent's row, and come in from its left.
      const turnY = by > ay ? b._top - 4 : b._top + barHeight + 4;
      d = `M ${ax} ${ay} H ${ax + CONNECTOR_STEP} V ${turnY} H ${bx - CONNECTOR_STEP} V ${by} H ${bx}`;
    }
    out.push({ id: b.id, d, cross: a._group !== b._group });
  }
  return out;
}
